export interface PartnershipPricingRow {
  id: string;
  partnerType: string;
  commercialModel: string;
  annualFeeInr: string;
  usageFee: string;
  apiAccess: string;
  typicalScopes: string;
  msmePricing: string;
}

export interface ApiUsagePricingRow {
  id: string;
  item: string;
  included: string;
  overage: string;
}

export const PARTNER_PROGRAM_SUMMARY =
  'Banks, auditors, verification agencies, and technology partners license Carbon Intelligence to onboard MSME cohorts, run green-finance workflows, and integrate via scoped REST APIs. Commercial terms are provided on request.';

export const PARTNERS_PAGE = {
  title: 'Partners',
  subtitle: 'Sustainow Carbon Intelligence',
  tagline: 'Distribution, assurance, and integration',
};

export const PARTNERS_PAGE_SECTIONS = {
  ecosystem: {
    title: 'Our partner ecosystem',
    workflowTitle: 'How the partnership model works',
    workflowLead:
      'Partners bring distribution and domain expertise; Carbon Intelligence provides the evidence layer that connects operations to finance and verification.',
  },
  partnerTypes: {
    title: 'Types of partners we work with',
    lead:
      'Carbon Intelligence is built for institutional and technology partnerships across green finance, supply chains, assurance, verification, and public programmes.',
  },
  benefits: {
    title: 'How partnerships benefit the ecosystem',
    lead:
      'Each partner type plays a distinct role, but the value compounds when MSMEs, financiers, assurance providers, and integrators connect through a single evidence layer.',
  },
  apiDocumentation: {
    title: 'Partner API documentation',
    lead:
      'Integrate via scoped REST APIs and webhooks. Machine-to-machine access uses partner API keys; portal dashboards use JWT after sign-in. Live scopes and endpoints are also available from the integration catalog.',
  },
};

export const PARTNERS_PAGE_CTA = {
  title: 'Partner with us',
  description:
    'Whether you are a bank rolling out a green-finance programme, an anchor enterprise onboarding suppliers, an auditor seeking structured evidence, or a technology integrator building on our APIs, we tailor commercial terms, API scopes, and integration support to your programme.',
  signInLabel: 'Partner portal sign in',
  registerLabel: 'Register as partner',
  workspaceLabel: 'Back to workspace',
  apiDocsLabel: 'API documentation',
  apiTeaserTitle: 'REST APIs and webhooks',
  apiTeaserDescription:
    'Authentication, scopes, endpoints, and integration examples for banks, auditors, and technology partners.',
  assistanceNote:
    'New integrators can register as a partner or email us for onboarding assistance.',
};

export interface PartnerTypeDetail {
  id: string;
  title: string;
  summary: string;
  capabilities: string[];
  ecosystemRole: string;
}

export interface EcosystemStakeholderBenefit {
  id: string;
  stakeholder: string;
  headline: string;
  benefits: string[];
}

export const PARTNER_ECOSYSTEM_INTRO =
  'Carbon Intelligence connects MSMEs, financial institutions, assurance providers, and technology integrators in a shared evidence layer. Partners bring distribution, policy, and domain expertise; the platform delivers measure → improve → prove → finance workflows, document AI, India government-factor data, and scoped REST APIs.';

export const PARTNER_TYPES: PartnerTypeDetail[] = [
  {
    id: 'bank_platform',
    title: 'Banks & NBFCs',
    summary:
      'Incentives and green-finance partners that onboard MSME borrower cohorts and originate qualified green loans with evidence-backed carbon scores.',
    capabilities: [
      'Portfolio carbon summaries for MSME borrowers',
      'Green loan eligibility and application workflows',
      'Webhooks for assessment completion and score changes',
      'Full partner API access with cohort monitoring',
    ],
    ecosystemRole:
      'Fund decarbonisation at scale by linking lending decisions to verified operational evidence instead of self-reported surveys.',
  },
  {
    id: 'anchor_enterprise',
    title: 'Anchor enterprises & supply-chain leads',
    summary:
      'Large buyers and programme sponsors that roll out supplier sustainability programmes across distributed MSME networks.',
    capabilities: [
      'Bulk supplier onboarding via invite campaigns and QR flows',
      'Scope 3–ready supplier reports and trend exports',
      'Adoption KPIs: registration, assessment completion, evidence upload',
      'Read APIs and bulk export packs for ESG reporting',
    ],
    ecosystemRole:
      'Turn supply-chain sustainability from periodic audits into continuous, comparable supplier performance data.',
  },
  {
    id: 'auditor',
    title: 'Government-accredited auditors',
    summary:
      'Assurance partners that review client MSME assessments, report readiness, and audit trails without replacing the MSME data owner.',
    capabilities: [
      'Read-only access to client assessments and report metadata',
      'Reviewer portal for assurance workflows',
      'Audit trail visibility on document processing and scores',
      'Scoped read APIs aligned with consent and DPDP requirements',
    ],
    ecosystemRole:
      'Provide independent assurance on sustainability claims while MSMEs retain ownership of raw documents and data.',
  },
  {
    id: 'verification_agency',
    title: 'Verification agencies',
    summary:
      'Specialists that validate carbon credits, green claims, and incentive applications using structured evidence packs from the platform.',
    capabilities: [
      'Verification packs built from report readiness and carbon summaries',
      'Read APIs for assessment metadata and trend history',
      'Optional per-pack verification workflows',
      'Integration with credits and claims review processes',
    ],
    ecosystemRole:
      'Close the loop between on-ground MSME activity and market-grade verification of environmental outcomes.',
  },
  {
    id: 'integration_partner',
    title: 'ERP & accounting integrators',
    summary:
      'Technology partners that connect Tally, accounting systems, and operational tools to Carbon Intelligence via certified connectors.',
    capabilities: [
      'Scoped write/read APIs for transaction summaries and MSME profiles',
      'Webhook delivery for document processing and score updates',
      'Sandbox environment for connector certification',
      'Reduced manual data entry for MSME operators',
    ],
    ecosystemRole:
      'Embed carbon intelligence into daily business systems so sustainability data is captured as a by-product of operations.',
  },
  {
    id: 'api_starter',
    title: 'API & analytics integrators',
    summary:
      'Fintech, analytics, and developer teams that build on scoped REST APIs and sandbox data to extend programme reach.',
    capabilities: [
      'Sandbox and production API keys with metered access',
      'Carbon summaries, MSME profiles, and report metadata endpoints',
      'Standard rate limits with elevated tiers on request',
      'OpenAPI specification for code generation and client tooling',
    ],
    ecosystemRole:
      'Extend the platform into adjacent products—dashboards, risk models, and regional programmes—without rebuilding core infrastructure.',
  },
  {
    id: 'institutional',
    title: 'Government & industry bodies',
    summary:
      'Institutions such as energy bureaus, state agencies, and sector associations that co-brand national or regional MSME programmes.',
    capabilities: [
      'Co-branded onboarding campaigns and regional MIS dashboards',
      'Benchmark data and sector analytics for policy design',
      'Train-the-trainer enablement and field onboarding support',
      'Pilot-to-scale rollout with adoption and outcome KPIs',
    ],
    ecosystemRole:
      'Align public programmes with measurable MSME outcomes—registration, evidence upload, score improvement, and finance readiness.',
  },
];

export const ECOSYSTEM_BENEFITS: EcosystemStakeholderBenefit[] = [
  {
    id: 'msme',
    stakeholder: 'MSMEs',
    headline: 'Affordable access to measure, improve, and prove impact',
    benefits: [
      'Often subsidised or zero-cost access when enrolled through a bank, anchor, or government programme',
      'Automated bill and document processing with India-specific emission factors',
      'Actionable recommendations, compliance readiness, and green-finance eligibility in one workspace',
      'Control over data sharing through consent-based partner access',
    ],
  },
  {
    id: 'partner_org',
    stakeholder: 'Partner organisations',
    headline: 'Distribution, evidence, and integration without building from scratch',
    benefits: [
      'Licensed platform with partner portal, usage metering, and API key management',
      'Cohort dashboards for adoption, carbon trends, and programme KPIs',
      'Scoped REST APIs and webhooks for machine-to-machine integration',
      'Commercial models tailored to banks, anchors, auditors, integrators, and institutions',
    ],
  },
  {
    id: 'ecosystem',
    stakeholder: 'The wider ecosystem',
    headline: 'A connected chain from operations to finance and verification',
    benefits: [
      'Supply-chain transparency: anchor enterprises see comparable supplier sustainability signals',
      'Green finance origination: banks link loan decisions to verified operational evidence',
      'Assurance at scale: auditors and verifiers work from structured packs, not ad-hoc spreadsheets',
      'Policy impact: institutions track state, sector, and cohort outcomes through shared MIS',
    ],
  },
];

export const PARTNER_ECOSYSTEM_FLOW = [
  'Partners onboard MSME cohorts through programmes, campaigns, or integrations.',
  'MSMEs upload bills and operational data; the platform extracts signals and carbon scores.',
  'Evidence flows to partners via dashboards, reports, and scoped APIs—with MSME consent.',
  'Outcomes unlock green finance, supplier programmes, verification, and continuous improvement.',
];

export const PARTNER_ECOSYSTEM_FLOW_STEPS = [
  {
    step: '01',
    title: 'Onboard cohorts',
    description: PARTNER_ECOSYSTEM_FLOW[0],
  },
  {
    step: '02',
    title: 'Capture evidence',
    description: PARTNER_ECOSYSTEM_FLOW[1],
  },
  {
    step: '03',
    title: 'Share with consent',
    description: PARTNER_ECOSYSTEM_FLOW[2],
  },
  {
    step: '04',
    title: 'Unlock outcomes',
    description: PARTNER_ECOSYSTEM_FLOW[3],
  },
];

export const PARTNERSHIP_PRICING_ROWS: PartnershipPricingRow[] = [
  {
    id: 'bank_platform',
    partnerType: 'Bank / NBFC (incentives partner)',
    commercialModel: 'Platform licence + cohort onboarding',
    annualFeeInr: '₹5,00,000 – ₹50,00,000 / year',
    usageFee: 'Optional ₹500 per qualified green-loan origination file',
    apiAccess: 'Full partner API (all scopes)',
    typicalScopes: 'msme:read, carbon:read, reports:read, transactions:summary, webhooks:manage',
    msmePricing: 'Subsidised or ₹0/month for programme MSMEs (anchor-sponsored)',
  },
  {
    id: 'anchor_enterprise',
    partnerType: 'Anchor enterprise / supply-chain lead',
    commercialModel: 'Supplier programme licence',
    annualFeeInr: '₹8,00,000 – ₹35,00,000 / year',
    usageFee: '₹75 – ₹150 per onboarded supplier / year (volume tiers)',
    apiAccess: 'Read APIs + bulk export packs',
    typicalScopes: 'msme:read, carbon:read, reports:read',
    msmePricing: 'Often bundled into supplier sustainability programme',
  },
  {
    id: 'auditor',
    partnerType: 'Government-accredited auditor',
    commercialModel: 'Assurance & verification seat',
    annualFeeInr: '₹2,00,000 – ₹8,00,000 / year',
    usageFee: '—',
    apiAccess: 'Reviewer portal + read APIs',
    typicalScopes: 'msme:read, carbon:read, reports:read',
    msmePricing: 'Not applicable (read-only on client MSMEs)',
  },
  {
    id: 'verification_agency',
    partnerType: 'Verification agency',
    commercialModel: 'Credits / claims verification',
    annualFeeInr: '₹1,50,000 – ₹6,00,000 / year',
    usageFee: '₹250 per verification pack (optional)',
    apiAccess: 'Read APIs + report metadata',
    typicalScopes: 'msme:read, carbon:read, reports:read',
    msmePricing: 'Billed to partner organisation',
  },
  {
    id: 'integration_partner',
    partnerType: 'ERP / accounting integrator',
    commercialModel: 'Technology partnership',
    annualFeeInr: '₹3,00,000 – ₹15,00,000 / year',
    usageFee: '₹50 / connected MSME / month above bundle',
    apiAccess: 'Scoped write/read via approved connectors',
    typicalScopes: 'msme:read, transactions:summary, webhooks:manage',
    msmePricing: 'MSMEs keep standard list pricing unless co-sponsored',
  },
  {
    id: 'api_starter',
    partnerType: 'API-only integrator',
    commercialModel: 'Developer / sandbox',
    annualFeeInr: '₹99,000 / year',
    usageFee: 'Standard rate limits; elevated tier on request',
    apiAccess: 'Sandbox + production keys',
    typicalScopes: 'msme:read, carbon:read (sandbox data)',
    msmePricing: 'N/A — partner bears platform fee',
  },
];

export const API_USAGE_PRICING_ROWS: ApiUsagePricingRow[] = [
  {
    id: 'msme_monitoring',
    item: 'MSME accounts monitored via API',
    included: '500 MSMEs / year (bank & anchor licences)',
    overage: '₹25 / MSME / month',
  },
  {
    id: 'api_calls',
    item: 'Partner API requests',
    included: '100,000 calls / month',
    overage: '₹0.15 per additional call',
  },
  {
    id: 'webhooks',
    item: 'Outbound webhook events',
    included: '10,000 events / month',
    overage: '₹0.50 per additional event',
  },
  {
    id: 'report_exports',
    item: 'Report readiness pulls',
    included: '2,000 / month',
    overage: '₹5 per additional pull',
  },
];

export const PARTNER_CONTACT_EMAIL = 'contact@sustainow.in';

export const PARTNER_SUPPORT_EMAIL =
  process.env.REACT_APP_PARTNER_SUPPORT_EMAIL || 'support@sustainow.in';

export const PARTNER_ORGANIZATION_TYPES = [
  { value: 'bank_incentives_partner', label: 'Bank / NBFC (incentives partner)' },
  { value: 'government_accredited_auditor', label: 'Government-accredited auditor' },
  { value: 'verification_agency', label: 'Verification agency' },
  { value: 'integration_partner', label: 'ERP / accounting integrator' },
  { value: 'other', label: 'Other technology partner' },
] as const;

export type PartnerOrganizationType = (typeof PARTNER_ORGANIZATION_TYPES)[number]['value'];
